/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Internal dependencies
 */
import PageHeader from '@Components/PageHeader';
import NotFound from '@Components/NotFound';
import ConfirmationDialog from '@Components/ConfirmationDialog';
import { CopyableText } from '@Components/CopyableText';
import Heading from '@Components/Heading';
import { useToastGuidGroup } from '@Hooks/useToastGuidGroup';
import { useVenues } from '@Hooks/useVenues';
import { useDeleteToastGuidGroup } from '@Hooks/useDeleteToastGuidGroup';
import ToastGuidGroupSheet from '@Components/ToastGuidGroups/ToastGuidGroupSheet';

export default function ToastGuidGroupDetails() {
  const { toastGuidGroupId } = useParams();
  const navigate = useNavigate();
  const {
    data: group,
    status,
    isError,
  } = useToastGuidGroup({
    id: toastGuidGroupId ?? '',
    include: 'guids.guid',
    enabled: !!toastGuidGroupId,
  });

  const venues = useVenues({ pageSize: -1 });

  const venueMap = useMemo(() => {
    return new Map((venues.data ?? []).map((v) => [v.id, v.name]));
  }, [venues.data]);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const deleteMutation = useDeleteToastGuidGroup();

  const handleDeleteConfirm = async () => {
    if (!group) return;

    try {
      await deleteMutation.mutateAsync({ id: group.id });
      toastSuccess(`"${group.name}" has been deleted.`);
      setDeleteDialogOpen(false);
      navigate('/settings/toast-guid-groups');
    } catch (err) {
      toastError(
        err instanceof Error
          ? err.message
          : 'Failed to delete Toast GUID Group',
      );
    }
  };

  const handleSheetSuccess = () => {
    toastSuccess(`"${group?.name}" has been updated.`);
  };

  if (isError) {
    return <NotFound />;
  }
  if (status === 'pending' || !group) {
    return <div>Loading...</div>;
  }

  const activeToastGuids = group.guids?.filter((tg) => !tg.deletedAt) ?? [];

  return (
    <div className="w-full">
      <PageHeader
        navList={[
          { name: 'Settings', href: '/settings' },
          { name: 'Toast GUID Groups', href: '/settings/toast-guid-groups' },
          {
            name: group.name,
            href: `/settings/toast-guid-groups/${toastGuidGroupId}`,
          },
        ]}
        title={group.name}
        label="TOAST GUID GROUP"
      />

      <div className="grid grid-cols-1 gap-12 xl:grid-cols-3">
        <div className="mb-12 flex flex-col space-y-8 xl:col-span-2">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Details</Heading>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSheetOpen(true)}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete
                </Button>
              </div>
            </div>
            <Card>
              <CardContent>
                <div className="flex flex-col space-y-2.5">
                  <div>
                    <div className="text-sm font-bold text-zinc-500">Name</div>
                    {group.name}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-zinc-500">
                      Description
                    </div>
                    {group.description || (
                      <span className="text-zinc-400">No description</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">
                Toast GUIDs ({activeToastGuids.length})
              </Heading>
            </div>
            <Card>
              <CardContent>
                {activeToastGuids.length === 0 ? (
                  <div className="py-4 text-center text-sm text-zinc-500">
                    No Toast GUIDs in this group
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>GUID</TableHead>
                        <TableHead>Venue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeToastGuids.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.guid?.name ?? '-'}</TableCell>
                          <TableCell className="font-mono text-sm">
                            {item.guid?.guid ?? item.guidId}
                          </TableCell>
                          <TableCell>
                            {item.guid?.venueId ? (
                              <CopyableText
                                text={
                                  venueMap.get(item.guid.venueId) ??
                                  item.guid.venueId
                                }
                                tooltipContent={item.guid.venueId}
                                copyValue={item.guid.venueId}
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ToastGuidGroupSheet
        toastGuidGroup={group}
        isOpen={sheetOpen}
        setIsOpen={setSheetOpen}
        onSuccess={handleSheetSuccess}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSubmit={handleDeleteConfirm}
        match={group.name}
        title="Delete Toast GUID Group"
        description={`Are you sure you want to delete "${group.name}"? This action cannot be undone.`}
        matchLabel="Type the group name to confirm"
        submitLabel="Delete"
        isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
